import { motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { cn } from 'lib/utils'
import type { ScanScreenshotPayload, WebsiteScanSection } from '@mondaygpt/types'

/** Re-exported as the local name the rest of dark/ uses. Section shape lives in shared types now. */
export type PolaroidSection = WebsiteScanSection

interface PolaroidProps {
  screenshot: ScanScreenshotPayload | null
  sections: PolaroidSection[]
  className?: string
}

const RENDERED_WIDTH = 200
// Matches a typical LLM call (~10s) so the scroll feels in sync with Taka
// "reading" the page; staying time-based keeps motion smooth even when
// profile partials arrive in a rapid burst.
const AUTO_SCROLL_DURATION_MS = 10000
/** A section is considered "in view" when its yOffset is within this distance of progress. */
const SECTION_VISIBILITY_WINDOW = 0.06

export const Polaroid: React.FC<PolaroidProps> = ({ screenshot, sections, className }) => {
  // ScreenshotOne returns the viewport size in JSON for full_page captures
  // (not the rendered height), so naturalWidth/Height off the loaded <img> is
  // the source of truth for the scroll math.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null)

  // Callback ref so the observer attaches the moment the viewport element
  // mounts (which only happens once `screenshot.url` is non-null and the
  // skeleton branch returns are skipped). A `useRef` + `useEffect([])` would
  // bind on the first render, when the ref is still null.
  const [viewportEl, setViewportEl] = useState<HTMLDivElement | null>(null)
  const [viewportHeight, setViewportHeight] = useState(0)
  useEffect(() => {
    if (!viewportEl || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height
      if (h) setViewportHeight(h)
    })
    ro.observe(viewportEl)
    return () => ro.disconnect()
  }, [viewportEl])

  const [progress, setProgress] = useState(0)
  useEffect(() => {
    if (!natural) return
    const start = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / AUTO_SCROLL_DURATION_MS)
      setProgress(t)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [natural])

  // All hooks must run on every render — keep `useMemo` above the early
  // return so the hook order stays stable across the skeleton → loaded
  // transition. The previous shape (memo below the early return) tripped
  // React's "change in the order of Hooks" guard the first time `screenshot`
  // became truthy and forced a hard unmount of the entire onboarding subtree
  // mid-scan, which read like the SSE was being aborted by the network.
  const visibleSection = useMemo(
    () => sections.find(s => Math.abs(s.yOffset - progress) < SECTION_VISIBILITY_WINDOW),
    [sections, progress]
  )

  if (!screenshot?.url) {
    return (
      <div
        data-testid="polaroid-skeleton"
        className={cn('card flex h-full w-[220px] flex-col p-2.5', className)}
      >
        <div className="min-h-0 w-[200px] flex-1 animate-pulse rounded-[8px] border border-line bg-bg-3" />
      </div>
    )
  }

  const ratio = natural ? natural.h / natural.w : 0
  const renderedHeight = RENDERED_WIDTH * ratio
  const maxOffset = Math.max(0, renderedHeight - viewportHeight)
  const translateY = -maxOffset * progress

  return (
    <div className={cn('card relative flex h-full w-[220px] flex-col p-2.5', className)}>
      {/* `min-h-0` keeps the absolutely-positioned full-page image from forcing
          its intrinsic height onto the flex parent (which would drag the
          discovery card next to it via items-stretch). */}
      <div
        ref={setViewportEl}
        className="relative min-h-0 w-[200px] flex-1 overflow-hidden rounded-[8px] border border-line bg-bg-3"
      >
        <motion.img
          src={screenshot.url}
          alt="preview"
          className="absolute top-0 left-0 block w-[200px] select-none"
          style={{ y: translateY }}
          draggable={false}
          onLoad={e => {
            const img = e.currentTarget
            if (!img.naturalWidth || !img.naturalHeight) return
            setNatural(curr => curr ?? { w: img.naturalWidth, h: img.naturalHeight })
          }}
        />
        {visibleSection && (
          <motion.div
            key={visibleSection.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-2 bottom-2 left-2 rounded-[8px] border border-line-bright bg-bg/85 px-2 py-1 text-[11px] font-medium text-paper backdrop-blur-sm"
          >
            {visibleSection.label}
          </motion.div>
        )}
      </div>
    </div>
  )
}
